import { ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { rangeClient, MenuDisplayStatus, MenuDto, MenuStatsDto, MenuType } from '../../../../app/services/api/range';
import useLogError from '../../../../hooks/useLogError';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps, TableSortDirection } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import { QuickFilterBar, QuickFilterGroup } from '../../../../components/shared/filters/QuickFilterBar';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { MenuDisplayStatusChip } from '../../components/MenuDisplayStatusChip';
import { Trash01 } from '../../../../assets';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';

const PAGE_SIZE = 20;
const DISPLAY_STATUSES: MenuDisplayStatus[] = [
  MenuDisplayStatus.Draft,
  MenuDisplayStatus.Scheduled,
  MenuDisplayStatus.Active,
  MenuDisplayStatus.Expired,
];

interface Props {
  onFilterChange?: (filters: { searchTerm?: string; displayStatus?: string; menuType?: string }) => void;
  initialSearchTerm?: string;
  initialDisplayStatus?: string;
  initialMenuType?: string;
  children: (...args: [{
    filterBar: ReactNode;
    advancedFilterContent: ReactNode;
    advancedFilterActions: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MenuDto>;
    isInitialLoading: boolean;
  }]) => ReactElement | null;
}

const formatDate = (value?: string | null): string => (
  value ? new Date(value).toLocaleDateString() : '-'
);

const formatSchedule = (menu: MenuDto, t: (...args: [string]) => string): string => {
  if (!menu.startDate && !menu.endDate) return '';
  if (menu.startDate && menu.endDate) return `${formatDate(menu.startDate)} - ${formatDate(menu.endDate)}`;
  if (menu.startDate) return `${t('from')} ${formatDate(menu.startDate)}`;
  return '';
};

const MenusListPageContent = ({
  onFilterChange,
  initialSearchTerm,
  initialDisplayStatus,
  initialMenuType,
  children,
}: Props) => {
  const { t } = useTranslation('range');
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    franchises,
    hasPermissionTo,
  } = useContext(UserContext);
  const { logError } = useLogError();

  const canView = hasPermissionTo(['MenuRead']);
  const canWrite = hasPermissionTo(['MenuWrite']);
  const [searchInput, setSearchInput] = useState(initialSearchTerm ?? '');
  const [searchText, setSearchText] = useState(initialSearchTerm ?? '');
  const [selectedDisplayStatus, setSelectedDisplayStatus] = useState<MenuDisplayStatus | ''>((initialDisplayStatus as MenuDisplayStatus) || '');
  const [selectedMenuType, setSelectedMenuType] = useState<MenuType | ''>((initialMenuType as MenuType) || '');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('asc');
  const [menus, setMenus] = useState<MenuDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<MenuStatsDto>({ totalMenus: 0, activeMenus: 0, scheduledMenus: 0, draftMenus: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<MenuDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setSearchInput(initialSearchTerm ?? '');
    setSearchText(initialSearchTerm ?? '');
    setSelectedDisplayStatus((initialDisplayStatus as MenuDisplayStatus) || '');
    setSelectedMenuType((initialMenuType as MenuType) || '');
  }, [initialSearchTerm, initialDisplayStatus, initialMenuType]);

  const franchiseNameById = useMemo(() => new Map(
    franchises
      .filter((franchise) => franchise.franchiseId !== undefined)
      .map((franchise) => [franchise.franchiseId as number, franchise.franchiseName]),
  ), [franchises]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    rangeClient.getMenus({
      search: searchText.trim() || undefined,
      menuType: selectedMenuType || undefined,
      displayStatus: selectedDisplayStatus || undefined,
      page: currentPage + 1,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDirection,
    })
      .then((result) => {
        if (!active) return;
        setMenus(result.data);
        setTotalCount(result.totalCount);
      })
      .catch((error) => {
        if (!active) return;
        logError(error);
        setMenus([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
        setHasLoadedOnce(true);
      });

    return () => { active = false; };
    // logError is intentionally omitted: useLogError returns a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, selectedMenuType, selectedDisplayStatus, currentPage, sortBy, sortDirection]);

  useEffect(() => {
    let active = true;
    setIsStatsLoading(true);
    rangeClient.getMenuStats()
      .then((result) => { if (active) setStats(result); })
      .catch((error) => { if (active) logError(error); })
      .finally(() => { if (active) setIsStatsLoading(false); });
    return () => { active = false; };
    // logError is intentionally omitted: useLogError returns a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilters = (filters: { searchTerm?: string; displayStatus?: string; menuType?: string } = {}) => onFilterChange?.(filters);

  const handleSearch = () => {
    setIsLoading(true);
    const nextSearch = searchInput.trim();
    setSearchText(nextSearch);
    setCurrentPage(0);
    updateFilters({ searchTerm: nextSearch, displayStatus: selectedDisplayStatus, menuType: selectedMenuType });
  };

  const handleClear = () => {
    setIsLoading(true);
    setSearchInput('');
    setSearchText('');
    setSelectedDisplayStatus('');
    setSelectedMenuType('');
    setCurrentPage(0);
    updateFilters({ searchTerm: '', displayStatus: '', menuType: '' });
  };

  const handleStatusChange = (value: readonly string[]) => {
    setIsLoading(true);
    const nextStatus = (value[0] as MenuDisplayStatus) || '';
    setSelectedDisplayStatus(nextStatus);
    setCurrentPage(0);
    updateFilters({ searchTerm: searchText, displayStatus: nextStatus, menuType: selectedMenuType });
  };

  const handleMenuTypeChange = (event: SelectChangeEvent) => {
    setIsLoading(true);
    const nextType = event.target.value as MenuType | '';
    setSelectedMenuType(nextType);
    setCurrentPage(0);
    updateFilters({ searchTerm: searchText, displayStatus: selectedDisplayStatus, menuType: nextType });
  };

  const handleSortChange = (nextSortBy: string, nextSortDirection: TableSortDirection) => {
    setIsLoading(true);
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
    setCurrentPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setIsLoading(true);
    setCurrentPage(nextPage);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      await rangeClient.deleteMenu(deleteCandidate.id);
      setMenus((current) => current.filter((menu) => menu.id !== deleteCandidate.id));
      setTotalCount((current) => Math.max(current - 1, 0));
      setDeleteCandidate(null);
      setFeedback({ message: t('menuDeleteSuccessMessage', 'Menu deleted successfully.'), severity: 'success' });
      setStats(await rangeClient.getMenuStats());
    } catch (error) {
      logError(error);
      setFeedback({ message: t('menuDeleteErrorMessage', 'Failed to delete menu.'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const statusLabelMap: Record<MenuDisplayStatus, string> = {
    [MenuDisplayStatus.Draft]: t('displayStatusDraft'),
    [MenuDisplayStatus.Scheduled]: t('displayStatusScheduled'),
    [MenuDisplayStatus.Active]: t('displayStatusActive'),
    [MenuDisplayStatus.Expired]: t('displayStatusExpired'),
  };

  const columns: TableColumn<MenuDto>[] = [
    { id: 'name', label: t('name'), sortable: true, sortKey: 'name', render: (row) => <Typography variant="textSM"
      fontWeight={500}>{row.name}</Typography> },
    {
      id: 'franchise',
      label: t('franchise'),
      render: (row) => franchiseNameById.get(row.franchiseId) ?? String(row.franchiseId),
    },
    { id: 'menuType', label: t('menuType'), sortable: true, sortKey: 'menuType', render: (row) => row.menuType ? t(`menuType${row.menuType}`) : '' },
    {
      id: 'servicePeriods',
      label: t('servicePeriods'),
      render: (row) => row.servicePeriodNames.length === 0 ? 0 : (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {row.servicePeriodNames.slice(0, 3).map((name) => (
            <Chip key={name}
              label={name}
              size="small"
              variant="outlined"
              sx={{ height: 24 }} />
          ))}
          {row.servicePeriodNames.length > 3 && (
            <Chip label={`+${row.servicePeriodNames.length - 3}`}
              size="small"
              sx={{ height: 24 }} />
          )}
        </Box>
      ),
    },
    { id: 'status', label: t('status'), render: (row) => <MenuDisplayStatusChip status={row.displayStatus} /> },
    { id: 'schedule', label: t('schedule'), render: (row) => formatSchedule(row, t) },
    { id: 'recipes', label: t('recipes'), sortable: true, sortKey: 'recipeCount', render: (row) => row.recipeCount },
    { id: 'createdDate', label: t('createdOn'), sortable: true, sortKey: 'createdDate', render: (row) => formatDate(row.createdDate) },
    { id: 'modifiedDate', label: t('modifiedOn'), sortable: true, sortKey: 'modifiedDate', render: (row) => formatDate(row.modifiedDate ?? row.createdDate) },
  ];

  if (canWrite) {
    columns.push({
      id: 'actions',
      label: t('actions'),
      render: (row) => (
        <IconButton
          aria-label={t('delete')}
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setDeleteCandidate(row);
          }}
        >
          <SvgIcon sx={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
        </IconButton>
      ),
    });
  }

  const table: TableSectionProps<MenuDto> = {
    title: t('menus'),
    columns,
    rows: menus,
    getRowId: (row) => row.id,
    onRowClick: canView ? (row) => navigate(`/range/menus/${row.id}`) : undefined,
    recordCount: totalCount,
    recordCountLabel: t('menus').toLowerCase(),
    loading: !hasLoadedOnce,
    loadingOverlay: isLoading && hasLoadedOnce,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={56}
        headerHeight={45}
        text={t('menus')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={() => navigate('/range/menus/new')}
        onAddIsCreating={false}
        showOnAdd={canWrite}
        addItemText={t('createMenu')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(Math.ceil(totalCount / PAGE_SIZE), 1),
    sortBy,
    sortDirection,
    onSortChange: handleSortChange,
    onPageChange: handlePageChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          fullWidth
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button fullWidth
          variant="primary"
          size="lg"
          onClick={handleSearch}
          startIcon={<SvgIcon><Search /></SvgIcon>}>{t('search')}</Button>
      </Grid>
      <Grid item
        lg={5}
        sm={12}
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          pt: 3,
          ml: { lg: 2, xs: 0 },
          transform: 'translateY(4px)',
        }}>
        <QuickFilterBar
          groups={[{
            id: 'status',
            options: [
              { id: '', label: t('all') },
              ...DISPLAY_STATUSES.map((status) => ({ id: status, label: statusLabelMap[status] })),
            ],
            value: [selectedDisplayStatus || ''],
            multiple: false,
            onChange: handleStatusChange,
          } as QuickFilterGroup]}
        />
      </Grid>
    </Grid>
  );

  const advancedFilterContent = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('menuType')}</InputLabel>
          <Select value={selectedMenuType}
            onChange={handleMenuTypeChange}
            label={t('menuType')}
            displayEmpty
            notched>
            <MenuItem value="">{t('allMenuTypes')}</MenuItem>
            {Object.values(MenuType).map((type) => <MenuItem key={type}
              value={type}>{t(`menuType${type}`)}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('status')}</InputLabel>
          <Select value={selectedDisplayStatus}
            onChange={(event) => handleStatusChange(event.target.value ? [event.target.value] : [])}
            label={t('status')}
            displayEmpty
            notched>
            <MenuItem value="">{t('allStatuses')}</MenuItem>
            {DISPLAY_STATUSES.map((status) => <MenuItem key={status}
              value={status}>{statusLabelMap[status]}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const advancedFilterActions = <Button variant="secondary"
    size="md"
    onClick={() => handleClear()}>{t('clear')}</Button>;

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile key="total"
        label={t('totalMenus')}
        value={stats.totalMenus}
        description={t('totalMenusDescription')}
        icon={<DescriptionOutlined />}
        variant="primary"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="active"
        label={t('activeMenus')}
        value={stats.activeMenus}
        description={t('activeMenusDescription')}
        icon={<CheckCircleOutlined />}
        variant="success"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="scheduled"
        label={t('scheduledMenus')}
        value={stats.scheduledMenus}
        description={t('scheduledMenusDescription')}
        icon={<EventAvailableOutlined />}
        variant="secondary"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="draft"
        label={t('draftMenus')}
        value={stats.draftMenus}
        description={t('draftMenusDescription')}
        icon={<ScheduleOutlined />}
        variant="warning"
        loading={isStatsLoading && !hasLoadedOnce} />,
    ],
  };

  return (
    <>
      {children({
        filterBar,
        advancedFilterContent,
        advancedFilterActions,
        headlineContent,
        table,
        isInitialLoading: isLoading && !hasLoadedOnce,
      })}
      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        icon={<Trash01 />}
        variant="bad"
        title={t('menuDeleteConfirmTitle')}
        message={t('menuDeleteConfirmMessage')}
        confirmButtonText={isDeleting ? t('deleting') : t('delete')}
        cancelButtonText={t('cancel')}
        isProcessing={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteCandidate(null)}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </>
  );
};

export default MenusListPageContent;
